import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronDown, Lock, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils/cn';
import { useAuth } from '@/lib/auth/useAuth';
import { hasAnyRole } from '@/lib/auth/permissions';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import { ALL_CLIENT_ROLES } from '@/types/auth';
import { useClientProductAccess } from '@/features/client-portal/useClientProductAccess';
import { NAV_GROUPS, type NavItem } from './navItems';
import logoMark from '@/assets/logo-mark.png';
import type { ResponseEnvelope } from '@/types/api';

const ADMIN_ROLES = [
  'AUTHMATECH_SUPER_ADMIN',
  'AUTHMATECH_ADMIN',
  'AUTHMATECH_SUPPORT',
  'AUTHMATECH_FINANCE',
] as const;

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user } = useAuth();
  const userRoles = user?.roles ?? [];

  const isAdmin = hasAnyRole(userRoles, [...ADMIN_ROLES]);
  const isClientUser = hasAnyRole(userRoles, ALL_CLIENT_ROLES);
  const { getStatus } = useClientProductAccess();

  const adminPendingQuery = useQuery({
    queryKey: ['admin', 'pending-count'],
    queryFn: () =>
      api
        .get<ResponseEnvelope<number>>(Endpoints.adminRequests.pendingCount)
        .then((r) => unwrap(r)),
    enabled: isAdmin,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const clientUnreadQuery = useQuery({
    queryKey: ['me', 'requests-unread'],
    queryFn: () =>
      api
        .get<ResponseEnvelope<number>>(Endpoints.me.requestsUnreadCount)
        .then((r) => unwrap(r)),
    enabled: isClientUser,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  function getBadgeCount(item: NavItem): number {
    if (item.badge === 'admin-pending') return adminPendingQuery.data ?? 0;
    if (item.badge === 'client-unread') return clientUnreadQuery.data ?? 0;
    return 0;
  }

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => !item.requiredRoles || hasAnyRole(userRoles, item.requiredRoles),
    ),
  })).filter((group) => group.items.length > 0);

  // Track collapsed state per group label — default all expanded
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  function toggleGroup(label: string) {
    setCollapsed((c) => ({ ...c, [label]: !c[label] }));
  }

  const displayName = user?.name || user?.preferredUsername || '';
  const userInitials = displayName
    ? displayName.split(' ').filter(Boolean).map((p) => p[0]?.toUpperCase() ?? '').slice(0, 2).join('')
    : '?';

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px] transition-opacity duration-200 lg:hidden',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
        aria-hidden
      />

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-sidebar flex-col',
          'transition-transform duration-200 ease-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0',
        )}
        style={{ background: 'linear-gradient(180deg, #06101f 0%, #091525 60%, #0b1a2e 100%)' }}
        aria-label="Primary navigation"
      >
        {/* Logo / Brand */}
        <div className="flex h-[60px] shrink-0 items-center justify-between border-b border-white/[0.06] px-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500/20 ring-1 ring-primary-400/30">
              <img
                src={logoMark}
                alt="Authmatech"
                className="h-5 w-5 object-contain"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>
            <div>
              <span className="block text-[14px] font-semibold tracking-tight text-white">
                Authmatech
              </span>
              <span className="block text-[9px] uppercase tracking-[0.15em] text-white/25 -mt-0.5">
                Console
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-white/30 transition-colors hover:bg-white/[0.07] hover:text-white/60 lg:hidden"
            aria-label="Close navigation"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Navigation groups */}
        <nav className="flex-1 overflow-y-auto px-2.5 py-4 scrollbar-hidden">
          {visibleGroups.map((group, idx) => {
            const isCollapsible = group.label !== null;
            const isCollapsed = isCollapsible && collapsed[group.label!];

            return (
              <div
                key={group.label ?? `group-${idx}`}
                className={cn(idx > 0 && 'mt-5')}
              >
                {group.label && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.label!)}
                    className="mb-1.5 flex w-full items-center justify-between px-2 py-0.5 transition-opacity hover:opacity-80"
                  >
                    <span className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-white/28">
                      {group.label}
                    </span>
                    {isCollapsible && (
                      <ChevronDown
                        className={cn(
                          'h-3 w-3 text-white/20 transition-transform duration-200',
                          isCollapsed && '-rotate-90',
                        )}
                        aria-hidden
                      />
                    )}
                  </button>
                )}
                {!isCollapsed && (
                  <ul className="space-y-0.5">
                    {group.items.map((item, itemIdx) => (
                      <SidebarLink
                        key={item.to}
                        item={item}
                        onNavigate={onClose}
                        count={getBadgeCount(item)}
                        delay={itemIdx * 30}
                        isLocked={
                          isClientUser && !!item.productCode && getStatus(item.productCode) === 'LOCKED'
                        }
                      />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="shrink-0 border-t border-white/[0.06] px-2.5 py-3">
          <div className="flex items-center gap-2.5 rounded-xl px-2 py-2.5">
            <div className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500/25 text-[11px] font-bold text-primary-300 ring-1 ring-primary-400/25">
              {userInitials}
              <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border-[1.5px] border-[#091525] bg-emerald-400" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-medium text-white/70">
                {displayName}
              </p>
              <p className="text-[10px] text-white/25">Authmatech Identity</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

function SidebarLink({
  item,
  onNavigate,
  count,
  delay = 0,
  isLocked = false,
}: {
  item: NavItem;
  onNavigate: () => void;
  count?: number;
  delay?: number;
  isLocked?: boolean;
}) {
  const Icon = item.icon;
  return (
    <li style={{ animationDelay: `${delay}ms` }}>
      <NavLink
        to={item.to}
        onClick={onNavigate}
        title={isLocked ? 'Available on request' : undefined}
        className={({ isActive }) =>
          cn(
            'group relative flex items-center gap-2.5 rounded-lg px-2 py-[7px] text-[13px] font-medium transition-all duration-150',
            isLocked
              ? 'text-white/28 hover:bg-white/[0.04] hover:text-white/40'
              : isActive
              ? 'bg-primary-500/[0.16] text-white'
              : 'text-white/45 hover:bg-white/[0.06] hover:text-white/75',
          )
        }
      >
        {({ isActive }) => (
          <>
            {/* Active glow bar — not shown for locked items */}
            {isActive && !isLocked && (
              <span
                className="absolute left-0 top-[6px] bottom-[6px] w-[3px] rounded-full bg-primary-400"
                style={{ boxShadow: '0 0 8px rgba(96,165,250,0.6)' }}
                aria-hidden
              />
            )}

            {/* Icon */}
            <span
              className={cn(
                'flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg transition-all duration-150',
                isLocked
                  ? 'text-white/20'
                  : isActive
                  ? 'bg-primary-400/25 text-primary-300'
                  : 'text-white/30 group-hover:bg-white/[0.07] group-hover:text-white/55',
              )}
            >
              <Icon className="h-[14px] w-[14px]" aria-hidden />
            </span>

            <span className="flex-1 truncate">{item.label}</span>

            {/* Product tag — surfaces the underlying Authmatech product in the nav */}
            {item.productTag && (
              <span
                className={cn(
                  'shrink-0 rounded px-1.5 py-px text-[8.5px] font-bold uppercase tracking-wide ring-1',
                  isLocked ? 'opacity-50' : '',
                  productTagClasses(item.productCode),
                )}
              >
                {item.productTag}
              </span>
            )}

            {/* Lock icon for locked products */}
            {isLocked && (
              <Lock className="h-3 w-3 shrink-0 text-white/20" aria-label="Available on request" />
            )}

            {/* Badge count — only for unlocked items */}
            {!isLocked && count != null && count > 0 && (
              <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
                {count > 99 ? '99+' : count}
              </span>
            )}
          </>
        )}
      </NavLink>
    </li>
  );
}

/** Tinted pill colour per product, matching the Badge tones (shield/trust/detect). */
function productTagClasses(productCode?: string): string {
  switch (productCode) {
    case 'SHIELD':
      return 'bg-emerald-400/15 text-emerald-300 ring-emerald-400/25';
    case 'TRUST':
      return 'bg-cyan-400/15 text-cyan-300 ring-cyan-400/25';
    case 'DETECT':
      return 'bg-orange-400/15 text-orange-300 ring-orange-400/25';
    default:
      return 'bg-primary-400/15 text-primary-300 ring-primary-400/25';
  }
}
